import { css } from 'styled-components';
import {
    ColorBeauty1,
    ColorBeautyFocus,
    ColorBeautyHover,
    ColorBeautyPressed,
    ColorBrandBlue1,
    ColorClubMatas1,
    ColorClubMatas2,
    ColorClubMatasFocus,
    ColorClubMatasHover,
    ColorClubMatasPlus1,
    ColorClubMatasPlus2,
    ColorClubMatasPlusFocus,
    ColorClubMatasPlusHover,
    ColorClubMatasPlusPressed,
    ColorClubMatasPressed,
    ColorNeutralBlack,
    ColorNeutralWhite,
    ColorOpacityOnDarkFocus,
    ColorOpacityOnDarkHover,
    ColorOpacityOnDarkPressed,
    ColorOpacityOnLightFocus,
    ColorOpacityOnLightHover,
    ColorOpacityOnLightPressed,
    ColorSubscription1,
    ColorSubscriptionActivated,
    ColorSubscriptionFocus,
    ColorSubscriptionHover,
    ColorWhiteFocus,
    ColorWhiteHover,
    ColorWhitePressed
} from 'autogen/design-tokens/tokens';
import type ButtonVariation from './types/buttonVariation';

/* Remember to add your variant in the obj at the bottom */

const sharedDisableStyling = css`
    cursor: not-allowed;
    background-color: ${({ theme }) => theme.ComponentsCtaDisabledBgDisabled};
    border: ${({ theme }) => theme.ComponentsCtaDisabledBorderDisabled};
    color: ${({ theme }) => theme.ComponentsCtaDisabledTextDisabled};

    &:not([href]),
    &:not([href]):not([tabindex]) {
        color: ${({ theme }) => theme.ComponentsCtaDisabledTextDisabled};
        &:hover {
            color: ${({ theme }) => theme.ComponentsCtaDisabledTextDisabled};
        }
    }
`;

const outlineInvertedStyling = css`
    outline-color: ${({ theme }) => theme.ColorBorderFocusRingInverted};
`;

// #region ButtonVariants
const primary: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${({ theme }) => theme.ComponentsCtaPrimaryTextDefault};
    background-color: ${({ theme }) => theme.ComponentsCtaPrimaryBgDefault};
    border: 1px solid ${({ theme }) => theme.ComponentsCtaPrimaryBorderDefault};

    // no href fix
    &:not([href]),
    &:not([href]):not([tabindex]) {
        color: ${({ theme }) => theme.ComponentsCtaPrimaryTextDefault};
        &:hover {
            color: ${({ theme }) => theme.ComponentsCtaPrimaryTextDefault};
        }
    }

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling};
              `
            : css`
                  &:hover {
                      background-color: ${({ theme }) => theme.ComponentsCtaPrimaryBgHover};
                      color: ${({ theme }) => theme.ComponentsCtaPrimaryTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaPrimaryBorderHover};
                  }

                  &:focus-visible {
                      background-color: ${({ theme }) => theme.ComponentsCtaPrimaryBgFocused};
                      color: ${({ theme }) => theme.ComponentsCtaPrimaryTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaPrimaryBorderFocus};
                  }

                  &:active {
                      background-color: ${({ theme }) => theme.ComponentsCtaPrimaryBgPressed};
                      border-color: ${({ theme }) => theme.ComponentsCtaPrimaryBorderPressed};
                  }
              `}
`;

const primaryInverted: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${({ theme }) => theme.ComponentsCtaPrimaryInvertedTextDefault};
    background-color: ${({ theme }) => theme.ComponentsCtaPrimaryInvertedBgDefault};
    border: 1px solid ${({ theme }) => theme.ComponentsCtaPrimaryInvertedBorderDefault};

    // no href fix
    &:not([href]),
    &:not([href]):not([tabindex]) {
        color: ${({ theme }) => theme.ComponentsCtaPrimaryInvertedTextDefault};
        &:hover {
            color: ${({ theme }) => theme.ComponentsCtaPrimaryInvertedTextDefault};
        }
    }

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling};
              `
            : css`
                  &:hover {
                      background-color: ${({ theme }) => theme.ComponentsCtaPrimaryInvertedBgHover};
                      color: ${({ theme }) => theme.ComponentsCtaPrimaryInvertedTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaPrimaryInvertedBorderHover};
                  }

                  &:focus-visible {
                      background-color: ${({ theme }) => theme.ComponentsCtaPrimaryInvertedBgFocused};
                      color: ${({ theme }) => theme.ComponentsCtaPrimaryInvertedTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaPrimaryInvertedBorderFocus};
                      ${outlineInvertedStyling}
                  }

                  &:active {
                      background-color: ${({ theme }) => theme.ComponentsCtaPrimaryInvertedBgPressed};
                      border-color: ${({ theme }) => theme.ComponentsCtaPrimaryInvertedBorderPressed};
                  }
              `}
`;

const secondary: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${({ theme }) => theme.ComponentsCtaSecondaryTextDefault};
    background-color: ${({ theme }) => theme.ComponentsCtaSecondaryBgDefault};
    border: 1px solid ${({ theme }) => theme.ComponentsCtaSecondaryBorderDefault};

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling};
              `
            : css`
                  &:hover {
                      background-color: ${({ theme }) => theme.ComponentsCtaSecondaryBgHover};
                      color: ${({ theme }) => theme.ComponentsCtaSecondaryTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaSecondaryBorderHover};
                  }

                  &:focus {
                      background-color: ${({ theme }) => theme.ComponentsCtaSecondaryBgFocused};
                      color: ${({ theme }) => theme.ComponentsCtaSecondaryTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaSecondaryBorderFocus};
                  }

                  &:active {
                      background-color: ${({ theme }) => theme.ComponentsCtaSecondaryBgPressed};
                      border-color: ${({ theme }) => theme.ComponentsCtaSecondaryBorderPressed};
                  }
              `}
`;

const secondaryInverted: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${({ theme }) => theme.ComponentsCtaSecondaryInvertedTextDefault};
    background-color: ${({ theme }) => theme.ComponentsCtaSecondaryInvertedBgDefault};
    border: 1px solid ${({ theme }) => theme.ComponentsCtaSecondaryInvertedBorderDefault};

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling};
              `
            : css`
                  &:hover {
                      background-color: ${({ theme }) => theme.ComponentsCtaSecondaryInvertedBgHover};
                      color: ${({ theme }) => theme.ComponentsCtaSecondaryInvertedTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaSecondaryInvertedBorderHover};
                  }

                  &:focus {
                      background-color: ${({ theme }) => theme.ComponentsCtaSecondaryInvertedBgFocused};
                      color: ${({ theme }) => theme.ComponentsCtaSecondaryInvertedTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaSecondaryInvertedBorderFocus};
                      ${outlineInvertedStyling}
                  }

                  &:active {
                      background-color: ${({ theme }) => theme.ComponentsCtaSecondaryInvertedBgPressed};
                      border-color: ${({ theme }) => theme.ComponentsCtaSecondaryInvertedBorderPressed};
                  }
              `}
`;

const tertiary: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${({ theme }) => theme.ComponentsCtaTertiaryTextDefault};
    background-color: ${({ theme }) => theme.ComponentsCtaTertiaryBgDefault};
    border: 1px solid ${({ theme }) => theme.ComponentsCtaTertiaryBorderDefault};

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling};
              `
            : css`
                  &:hover {
                      background-color: ${({ theme }) => theme.ComponentsCtaTertiaryBgHover};
                      color: ${({ theme }) => theme.ComponentsCtaTertiaryTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaTertiaryBorderHover};
                  }

                  &:focus {
                      background-color: ${({ theme }) => theme.ComponentsCtaTertiaryBgFocused};
                      color: ${({ theme }) => theme.ComponentsCtaTertiaryTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaTertiaryBorderFocus};
                  }

                  &:active {
                      background-color: ${({ theme }) => theme.ComponentsCtaTertiaryBgPressed};
                      border-color: ${({ theme }) => theme.ComponentsCtaTertiaryBorderPressed};
                  }
              `}
`;

const tertiaryInverted: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${({ theme }) => theme.ComponentsCtaTertiaryInvertedTextDefault};
    background-color: ${({ theme }) => theme.ComponentsCtaTertiaryInvertedBgDefault};
    border: 1px solid ${({ theme }) => theme.ComponentsCtaTertiaryInvertedBorderDefault};

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling};
              `
            : css`
                  &:hover {
                      background-color: ${({ theme }) => theme.ComponentsCtaTertiaryInvertedBgHover};
                      color: ${({ theme }) => theme.ComponentsCtaTertiaryInvertedTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaTertiaryInvertedBorderHover};
                  }

                  &:focus {
                      background-color: ${({ theme }) => theme.ComponentsCtaTertiaryInvertedBgFocused};
                      color: ${({ theme }) => theme.ComponentsCtaTertiaryInvertedTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaTertiaryInvertedBorderFocus};
                      ${outlineInvertedStyling}
                  }

                  &:active {
                      background-color: ${({ theme }) => theme.ComponentsCtaTertiaryInvertedBgPressed};
                      border-color: ${({ theme }) => theme.ComponentsCtaTertiaryInvertedBorderPressed};
                  }
              `}
`;

const ghost: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${({ theme }) => theme.ComponentsCtaGhostTextDefault};
    background-color: ${({ theme }) => theme.ComponentsCtaGhostBgDefault};
    border: 1px solid ${({ theme }) => theme.ComponentsCtaGhostBorderDefault};

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling};
              `
            : css`
                  &:hover {
                      background-color: ${({ theme }) => theme.ComponentsCtaGhostBgHover};
                      color: ${({ theme }) => theme.ComponentsCtaGhostTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaGhostBorderHover};
                  }

                  &:focus {
                      background-color: ${({ theme }) => theme.ComponentsCtaGhostBgFocused};
                      color: ${({ theme }) => theme.ComponentsCtaGhostTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaGhostBorderFocus};
                  }

                  &:active {
                      background-color: ${({ theme }) => theme.ComponentsCtaGhostBgPressed};
                      border-color: ${({ theme }) => theme.ComponentsCtaGhostBorderPressed};
                  }
              `}
`;

const ghostInverted: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${({ theme }) => theme.ComponentsCtaGhostInvertedTextDefault};
    background-color: ${({ theme }) => theme.ComponentsCtaGhostInvertedBgDefault};
    border: 1px solid ${({ theme }) => theme.ComponentsCtaGhostInvertedBorderDefault};

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling};
              `
            : css`
                  &:hover {
                      background-color: ${({ theme }) => theme.ComponentsCtaGhostInvertedBgHover};
                      color: ${({ theme }) => theme.ComponentsCtaGhostInvertedTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaGhostInvertedBorderHover};
                  }

                  &:focus {
                      background-color: ${({ theme }) => theme.ComponentsCtaGhostInvertedBgFocused};
                      color: ${({ theme }) => theme.ComponentsCtaGhostInvertedTextDefault};
                      border-color: ${({ theme }) => theme.ComponentsCtaGhostInvertedBorderFocus};
                      ${outlineInvertedStyling}
                  }

                  &:active {
                      background-color: ${({ theme }) => theme.ComponentsCtaGhostInvertedBgPressed};
                      border-color: ${({ theme }) => theme.ComponentsCtaGhostInvertedBorderPressed};
                  }
              `}
`;

const negative: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${ColorNeutralBlack};
    background-color: ${ColorNeutralWhite};

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling}
              `
            : css`
                  &:hover {
                      background-color: ${ColorWhiteHover};
                  }

                  &:focus {
                      background-color: ${ColorWhiteFocus};
                  }

                  &:active {
                      background-color: ${ColorWhitePressed};
                  }
              `}
`;

const ghostOnLight: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${ColorNeutralBlack};
    background-color: transparent;

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling}
              `
            : css`
                  &:hover {
                      background-color: ${ColorOpacityOnLightHover};
                  }

                  &:focus {
                      background-color: ${ColorOpacityOnLightFocus};
                  }

                  &:active {
                      background-color: ${ColorOpacityOnLightPressed};
                  }
              `}
`;

const ghostOnDark: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${ColorNeutralWhite};
    background-color: transparent;

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling}
              `
            : css`
                  &:hover {
                      background-color: ${ColorOpacityOnDarkHover};
                  }

                  &:focus {
                      background-color: ${ColorOpacityOnDarkFocus};
                  }

                  &:active {
                      background-color: ${ColorOpacityOnDarkPressed};
                  }
              `}
`;

const club: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${ColorNeutralBlack};
    background-color: ${ColorClubMatas2};

    ${({ $disabled }) =>
        $disabled
            ? css`
                  background-color: ${ColorClubMatas1};
                  ${sharedDisableStyling}
              `
            : css`
                  &:hover {
                      background-color: ${ColorClubMatasHover};
                  }

                  &:focus {
                      background-color: ${ColorClubMatasFocus};
                  }

                  &:active {
                      background-color: ${ColorClubMatasPressed};
                  }
              `}
`;

const plus: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${ColorNeutralBlack};
    background-color: ${ColorClubMatasPlus2};

    ${({ $disabled }) =>
        $disabled
            ? css`
                  background-color: ${ColorClubMatasPlus1};
                  ${sharedDisableStyling}
              `
            : css`
                  &:hover {
                      background-color: ${ColorClubMatasPlusHover};
                  }

                  &:focus {
                      background-color: ${ColorClubMatasPlusFocus};
                  }

                  &:active {
                      background-color: ${ColorClubMatasPlusPressed};
                  }
              `}
`;

const beauty: ButtonVariation = css<{ theme }>`
    color: ${ColorNeutralBlack};
    background-color: ${ColorBeauty1};

    ${({ theme }) =>
        theme.disabled
            ? css`
                  ${sharedDisableStyling}
              `
            : css`
                  &:hover {
                      background-color: ${ColorBeautyHover};
                  }

                  &:focus {
                      background-color: ${ColorBeautyFocus};
                  }

                  &:active {
                      background-color: ${ColorBeautyPressed};
                  }
              `}
`;

const subscribe: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${ColorNeutralBlack};
    background-color: ${ColorSubscription1};

    ${({ theme }) =>
        (theme as any).disabled
            ? css`
                  ${sharedDisableStyling}
              `
            : css`
                  &:hover {
                      background-color: ${ColorSubscriptionHover};
                  }

                  &:focus {
                      background-color: ${ColorSubscriptionFocus};
                  }

                  &:active {
                      background-color: ${ColorSubscriptionActivated};
                  }
              `}
`;

const primaryText: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${ColorNeutralBlack};
    background-color: transparent;
    text-decoration: underline;

    ${({ theme }) =>
        (theme as any).disabled
            ? css`
                  ${sharedDisableStyling}
              `
            : css`
                  &:focus {
                      outline-color: ${ColorNeutralBlack};
                  }
              `}
`;

const primaryTextInline: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${ColorNeutralBlack};
    background-color: transparent;
    text-decoration: underline;
    padding-inline: 0;

    ${({ theme }) =>
        (theme as any).disabled
            ? css`
                  ${sharedDisableStyling}
              `
            : css`
                  &:focus {
                      outline-color: ${ColorNeutralBlack};
                  }
              `}
`;

const primaryTextNegative: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${ColorNeutralWhite};
    background-color: transparent;
    text-decoration: underline;

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling}
              `
            : css`
                  &:focus {
                      outline-color: ${ColorNeutralWhite};
                  }
              `}
`;

const outline: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${ColorNeutralBlack};
    background-color: transparent;
    border: 1px solid ${ColorBrandBlue1};

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling}
              `
            : css`
                  &:hover {
                      background-color: ${ColorOpacityOnLightHover};
                  }

                  &:focus {
                      background-color: ${ColorOpacityOnLightFocus};
                  }

                  &:active {
                      background-color: ${ColorOpacityOnLightPressed};
                  }
              `}
`;

const navigation: ButtonVariation = css<{ $disabled: boolean }>`
    color: ${({ theme }) => theme.ComponentsCtaNavigationText};
    background-color: ${({ theme }) => theme.ComponentsCtaNavigationBg};

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling};
              `
            : css`
                &:hover {
                    background-color: ${({ theme }) => theme.ComponentsCtaNavigationBgHover};
                    color: ${({ theme }) => theme.ComponentsCtaNavigationText};
                }

                &:focus {
                    background-color: ${({ theme }) => theme.ComponentsCtaNavigationBgFocus};
                    color: ${({ theme }) => theme.ComponentsCtaNavigationText};
                    ${outlineInvertedStyling}
                }

                &:active {
                    background-color: ${({ theme }) => theme.ComponentsCtaNavigationBgPressed};
                }
              `}
`;
// #endregion ButtonVariants

/* Remember to add your variant in the obj below */

/**
 * @deprecated don't use deprecated variants anymore
 */
const deprecatedButtonVariants = {
    plus,
    beauty,
    subscribe,
    club,
    primaryText,
    primaryTextInline,
    primaryTextNegative,
    ghostOnLight,
    ghostOnDark,
    negative,
    outline
} as const;

const buttonVariants = {
    primary,
    secondary,
    tertiary,
    ghost,
    primaryInverted,
    secondaryInverted,
    tertiaryInverted,
    ghostInverted,
    navigation,
    ...deprecatedButtonVariants
} as const;

/**
 * @deprecated don't use deprecated variants anymore
 */
type deprecatedButtonVariantKeys = keyof typeof deprecatedButtonVariants;

export type buttonVariantKeys = keyof typeof buttonVariants | deprecatedButtonVariantKeys;

export type ButtonVariant = buttonVariantKeys | null;

export const buttonVariantStyling = css<{ variant?: ButtonVariant }>`
    ${({ variant }) => variant && buttonVariants[variant]}
`;

export default buttonVariants;
